import {
  ref,
  get,
  set,
  update,
  remove,
  onValue,
  query,
  orderByChild,
  equalTo,
  off,
} from 'firebase/database';
import { rtdb } from '../../config/firebase';
import type { User } from '../../types';
import { snapshotToArray, stripUndefined } from './helpers';

const PATH = 'users';

type Unsubscribe = () => void;

const normalizeUser = (id: string, data: any): User => ({
  ...data,
  id,
  isAdmin: Boolean(data.isAdmin),
  isFamily: Boolean(data.isFamily),
  isFamilyMember: Boolean(data.isFamilyMember),
});

export const getAllUsers = async (): Promise<User[]> => {
  const snapshot = await get(ref(rtdb, PATH));
  return snapshotToArray<User>(snapshot.val(), normalizeUser);
};

export const getUserById = async (userId: string): Promise<User | null> => {
  const snapshot = await get(ref(rtdb, `${PATH}/${userId}`));
  return snapshot.exists() ? normalizeUser(userId, snapshot.val()) : null;
};

export const saveUser = async (user: User): Promise<void> => {
  const { id: _id, ...rest } = user;
  await set(ref(rtdb, `${PATH}/${user.id}`), stripUndefined(rest));
};

export const updateUser = async (
  userId: string,
  updates: Partial<User>
): Promise<void> => {
  await update(ref(rtdb, `${PATH}/${userId}`), stripUndefined(updates));
};

export const deleteUser = async (userId: string): Promise<void> => {
  await remove(ref(rtdb, `${PATH}/${userId}`));
};

export const getUsersByStatus = async (
  status: 'pending' | 'approved' | 'blocked'
): Promise<User[]> => {
  const q = query(ref(rtdb, PATH), orderByChild('status'), equalTo(status));
  const snapshot = await get(q);
  return snapshotToArray<User>(snapshot.val(), normalizeUser);
};

export const subscribeToUsers = (
  callback: (users: User[]) => void,
  onError?: (error: Error) => void
): Unsubscribe => {
  const r = ref(rtdb, PATH);
  const handler = onValue(
    r,
    (snapshot) => {
      callback(snapshotToArray<User>(snapshot.val(), normalizeUser));
    },
    onError
  );
  return () => off(r, 'value', handler);
};

export const subscribeToUser = (
  userId: string,
  callback: (user: User | null) => void
): Unsubscribe => {
  const r = ref(rtdb, `${PATH}/${userId}`);
  const handler = onValue(r, (snapshot) => {
    callback(snapshot.exists() ? normalizeUser(userId, snapshot.val()) : null);
  });
  return () => off(r, 'value', handler);
};

export const getUserByEmail = async (email: string): Promise<User | null> => {
  const q = query(
    ref(rtdb, PATH),
    orderByChild('email'),
    equalTo(email.toLowerCase())
  );
  const snapshot = await get(q);
  const users = snapshotToArray<User>(snapshot.val(), normalizeUser);
  return users[0] ?? null;
};

export const subscribeToUserByEmail = (
  email: string,
  callback: (user: User | null) => void
): Unsubscribe => {
  const q = query(
    ref(rtdb, PATH),
    orderByChild('email'),
    equalTo(email.toLowerCase())
  );
  const handler = onValue(q, (snapshot) => {
    const users = snapshotToArray<User>(snapshot.val(), normalizeUser);
    callback(users[0] ?? null);
  });
  return () => off(q, 'value', handler);
};

/**
 * Migrate all foreign key references from an old user ID to a new user ID.
 * Uses a multi-path update for atomicity.
 */
export const migrateUserReferences = async (
  oldId: string,
  newId: string
): Promise<void> => {
  const refMappings: { path: string; field: string }[] = [
    { path: 'bookings', field: 'userId' },
    { path: 'bragPosts', field: 'userId' },
    { path: 'reviews', field: 'userId' },
    { path: 'invitations', field: 'invitedBy' },
    { path: 'users', field: 'referredBy' },
    { path: 'contentPages', field: 'lastUpdatedBy' },
  ];

  const updates: Record<string, any> = {};

  for (const { path, field } of refMappings) {
    const snapshot = await get(
      query(ref(rtdb, path), orderByChild(field), equalTo(oldId))
    );
    const value = snapshot.val();
    if (!value) continue;
    for (const id of Object.keys(value)) {
      updates[`${path}/${id}/${field}`] = newId;
    }
  }

  if (Object.keys(updates).length > 0) {
    await update(ref(rtdb), updates);
  }
};
